import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type AuditLog = components["schemas"]["BillAuditLogResponse"];

interface BillAuditLogTableProps {
  billId: string;
}

export function BillAuditLogTable({ billId }: BillAuditLogTableProps) {
  const pagination = useCursorPagination({ initialLimit: 50 });

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchLogs() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/bills/{billId}/audit-logs", {
        params: {
          path: { billId },
          query: {
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load audit logs.");
      } else {
        setLogs(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchLogs();
    return () => {
      cancelled = true;
    };
  }, [billId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Transition</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Snapshot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground h-24 text-center">
                  No audit logs.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <Fragment key={log.id}>
                  <TableRow>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatDateTime(log.createdAt)}
                    </TableCell>
                    <TableCell className="font-medium">{log.action}</TableCell>
                    <TableCell>
                      <StateTransition from={log.fromState} to={log.toState} />
                    </TableCell>
                    <TableCell>
                      <ActorBadge actorId={log.actorId} actorType={log.actorType} />
                    </TableCell>
                    <TableCell>
                      {log.billSnapshot ? (
                        <button
                          type="button"
                          onClick={() => setExpandedId(expandedId === log.id ? null : log.id ?? null)}
                          className="text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-2 decoration-dotted cursor-pointer"
                        >
                          {expandedId === log.id ? "Hide" : "Show"}
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedId === log.id && log.billSnapshot && (
                    <TableRow>
                      <TableCell colSpan={5} className="bg-muted/40">
                        <pre className="overflow-x-auto text-xs font-mono whitespace-pre-wrap break-all">
                          {formatJson(log.billSnapshot)}
                        </pre>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        limitOptions={[20, 50, 100]}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}

function StateTransition({
  from,
  to,
}: {
  from: AuditLog["fromState"];
  to: AuditLog["toState"];
}) {
  if (!from && !to) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-sm">
      {from ? <Badge variant="outline">{from}</Badge> : <span className="text-muted-foreground">—</span>}
      <span className="mx-1.5 text-muted-foreground">→</span>
      {to ? <Badge variant="secondary">{to}</Badge> : <span className="text-muted-foreground">—</span>}
    </span>
  );
}

function ActorBadge({
  actorId,
  actorType,
}: {
  actorId: AuditLog["actorId"];
  actorType: AuditLog["actorType"];
}) {
  if (actorType === "System" || !actorId) {
    return <Badge variant="outline">System</Badge>;
  }
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}

function formatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
